import type { ScriptProgress } from '../lib/api.js';
import { useScriptRun } from '../lib/useScriptRun.js';
import { Icon } from './Icon.js';

/**
 * WHAT IS HAPPENING TO THE SCREENPLAY, from the worker's own record.
 *
 * The tab used to print one word — "Parsing…" — for every state between queued
 * and failed, and it polled for scenes to decide when to stop. The pipeline has
 * been recording the truth all along in `script_breakdown_pipeline_stage`; it
 * simply had no route out until now.
 *
 * TWO STAGES, NOT THE DESIGN'S SIX. `14 Script Analysis` draws six — reading,
 * identifying scenes, extracting requirements, combining them, searching
 * locations, preparing results — ticking along on a timer. Only two of those
 * are things this service runs as a pipeline (`PARSE` and `EXTRACT`), and the
 * last two belong to matching, which a producer starts themselves and which
 * would therefore sit at "in progress" for ever. Six rows would be a better
 * picture of a worse lie: this draws what is measured, and says plainly where
 * the automatic part stops and the producer's next decision begins.
 */
const STAGE_LABEL: Record<string, string> = {
  PARSE: 'Reading the screenplay and identifying scenes',
  EXTRACT: 'Extracting location requirements',
};

const STAGE_NOTE: Record<string, string> = {
  PARSE: 'Runs by itself the moment a file lands.',
  EXTRACT: 'Runs when you press Analyse with AI — one request for the whole screenplay.',
};

function stageLabel(stage: string): string {
  // A stage this build does not know about is a newer worker talking to an
  // older tab. Showing its raw name is ugly and true; hiding the row would
  // under-report what is running.
  return STAGE_LABEL[stage] ?? stage;
}

/** The states the worker writes. Anything else is shown as itself. */
function stateOf(status: string): 'done' | 'running' | 'failed' | 'waiting' {
  if (status === 'COMPLETED') return 'done';
  if (status === 'RUNNING') return 'running';
  if (status === 'FAILED') return 'failed';

  return 'waiting';
}

export function ScriptProgressPanel({
  scriptVersionId,
  testId = 'script-progress',
}: {
  scriptVersionId: string;
  testId?: string;
}) {
  // Polled only while something is moving, stopped by the answer — see the hook.
  const { progress: data } = useScriptRun(scriptVersionId);

  // Nothing has been run on this version — a pasted screenplay that parsed
  // before the tab asked. There is no progress to report and no box to draw.
  if (!data || data.stages.length === 0) return null;

  return (
    <div
      className="card1"
      data-testid={testId}
      data-status={data.status}
      style={{ padding: '18px 20px', marginTop: 18 }}
    >
      <p className="colh" style={{ margin: 0 }}>
        {data.status === 'FAILED' ? 'The breakdown stopped' : 'Analysing your script'}
      </p>
      <p className="mm" style={{ fontSize: 12.5, margin: '6px 0 0', lineHeight: 1.6 }}>
        {data.status === 'FAILED'
          ? 'Nothing is lost — the file is stored and the scenes already read are still here.'
          : 'This runs in the background. You can leave this page; the work carries on without it.'}
      </p>

      <ol style={{ listStyle: 'none', margin: '14px 0 0', padding: 0 }}>
        {data.stages.map((stage) => (
          <StageRow key={stage.stage} stage={stage} />
        ))}
      </ol>
    </div>
  );
}

function StageRow({ stage }: { stage: ScriptProgress['stages'][number] }) {
  const state = stateOf(stage.status);
  // The count is real for extraction, whose stage row records how many scenes
  // came back accounted for out of how many were sent — one request covers the
  // whole screenplay, so this is progress THROUGH the answer, not through a
  // queue of calls. A parse has no unit to count, so it gets no fraction rather
  // than a fabricated one.
  const counted = stage.totalUnits !== null && stage.totalUnits > 0;

  return (
    <li
      data-testid={`script-stage-${stage.stage}`}
      data-state={state}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '13px 14px',
        borderTop: '1px solid var(--color-divider)',
        background: state === 'running' ? 'var(--color-surface)' : 'transparent',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 17,
          height: 17,
          marginTop: 2,
          flex: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: state === 'done' ? 'var(--ok)' : 'var(--color-accent)',
        }}
      >
        {state === 'done' ? (
          <Icon name="check" size={17} strokeWidth={2.6} />
        ) : state === 'running' ? (
          <span style={{ width: 11, height: 11, background: 'var(--color-accent)' }} />
        ) : state === 'failed' ? (
          <span style={{ fontWeight: 700, color: 'var(--danger)' }}>!</span>
        ) : (
          <span
            style={{ width: 9, height: 9, border: '2px solid var(--color-divider)' }}
          />
        )}
      </span>

      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: state === 'running' ? 700 : 600,
            color: state === 'waiting' ? 'var(--color-neutral-500)' : 'var(--ink)',
          }}
        >
          {stageLabel(stage.stage)}
        </span>
        <span className="mm" style={{ display: 'block', fontSize: 12, marginTop: 3 }}>
          {STAGE_NOTE[stage.stage] ?? ''}
        </span>
      </span>

      <span
        className="mm"
        style={{ fontSize: 12, flex: 'none', whiteSpace: 'nowrap' }}
        data-testid={`script-stage-${stage.stage}-status`}
      >
        {state === 'done'
          ? 'Done'
          : state === 'failed'
            ? (stage.failureCode ?? 'Stopped')
            : state === 'running'
              ? counted
                ? `${String(stage.completedUnits)} of ${String(stage.totalUnits)} scenes`
                : 'In progress…'
              : 'Waiting'}
      </span>
    </li>
  );
}
